// Every gameplay formula (architecture §1.2, §14: formulas live only here). Each function is pure
// and cites the mechanics.md section it implements. Parameters default to the config defaults in
// constants.ts; the server passes its loaded config values instead.
//
// Units: distances in world units (u), time in seconds unless a name says Ms or Ticks, mass is
// unitless (r = 10·√m).

import {
  BOOST_REFERENCE_DT_SEC,
  BOT_DEFAULTS,
  BOT_TUNING,
  CAMERA_EASE,
  MASS_HISTORY,
  MODE_DEFAULTS,
  RADIUS_PER_SQRT_MASS,
  REFERENCE_WORLD_SIZE,
  SIM_DEFAULTS,
  WHEEL_ZOOM,
} from './constants.js';
import { clamp } from './math/clamp.js';
import type { Rng } from './math/rng.js';
import type { Vec2 } from './math/vec2.js';

const TAU = Math.PI * 2;
const SQRT1_2 = Math.SQRT1_2;

// ---------------------------------------------------------------------------------------------
// §0.1 Time
// ---------------------------------------------------------------------------------------------

/** `ticks = round(seconds × tickRate)` (mechanics §0.1). E.g. 0.52 s → 13 ticks at 25 Hz. */
export function secondsToTicks(seconds: number, tickRate: number = SIM_DEFAULTS.tickRate): number {
  return Math.round(seconds * tickRate);
}

/** One tick's duration, `dt = 1 / tickRate` (mechanics §2). */
export function tickDt(tickRate: number = SIM_DEFAULTS.tickRate): number {
  return 1 / tickRate;
}

// ---------------------------------------------------------------------------------------------
// §0.4 Boosted motion
// ---------------------------------------------------------------------------------------------

export interface BoostParams {
  readonly retainPer40ms: number;
  readonly maxStepPer40ms: number;
  readonly endBelow: number;
}

/**
 * Distance a boost moves in one tick (mechanics §0.4):
 * `step = min(B × (1 − retain^(dt/0.04)), maxStep × dt/0.04)`, which at 40 ms is
 * `min(B × 0.1, 78)`.
 */
export function boostStep(
  remainingU: number,
  dtSec: number = tickDt(),
  p: BoostParams = SIM_DEFAULTS.boost,
): number {
  const k = dtSec / BOOST_REFERENCE_DT_SEC;
  // B − B·retain^k rather than B·(1 − retain^k): 1 − 0.9 is not exact in binary, this is (78 u).
  const retained = remainingU * (k === 1 ? p.retainPer40ms : Math.pow(p.retainPer40ms, k));
  return Math.min(remainingU - retained, p.maxStepPer40ms * k);
}

/** The boost left after moving `stepU`; 0 (ended, leftover discarded) once below `endBelow`. */
export function boostRemainingAfter(
  remainingU: number,
  stepU: number,
  endBelow: number = SIM_DEFAULTS.boost.endBelow,
): number {
  const left = remainingU - stepU;
  return left < endBelow ? 0 : left;
}

export interface BoostProgress {
  /** Distance covered so far (u). */
  distanceU: number;
  /** Boost still left (u); 0 once ended. */
  remainingU: number;
  /** Ticks simulated (stops early when the boost ends). */
  ticks: number;
  ended: boolean;
}

/**
 * Runs a boost of `boostU` for up to `ticks` ticks (mechanics §0.4, Appendix C): 780 u covers
 * 78.0 u after 1 tick, 319.4 after 5, 508.0 after 10, and ends after 64 ticks at 779.1 u.
 */
export function simulateBoost(
  boostU: number,
  ticks: number,
  dtSec: number = tickDt(),
  p: BoostParams = SIM_DEFAULTS.boost,
): BoostProgress {
  let remainingU = boostU < p.endBelow ? 0 : boostU;
  let distanceU = 0;
  let n = 0;
  while (n < ticks && remainingU > 0) {
    const step = boostStep(remainingU, dtSec, p);
    distanceU += step;
    remainingU = boostRemainingAfter(remainingU, step, p.endBelow);
    n++;
  }
  return { distanceU, remainingU, ticks: n, ended: remainingU === 0 };
}

/** Ticks until a boost of `boostU` ends (64 for 780 u at 25 Hz). */
export function boostDurationTicks(
  boostU: number,
  dtSec: number = tickDt(),
  p: BoostParams = SIM_DEFAULTS.boost,
): number {
  return simulateBoost(boostU, Number.MAX_SAFE_INTEGER, dtSec, p).ticks;
}

export interface BoostVector {
  dirX: number;
  dirY: number;
  remainingU: number;
}

/**
 * Adds a push to a boost as vectors (mechanics §17.4, Experimental viruses):
 * `B_vec ← B_vec + pushU × pushDir` (pushDir a unit vector); the direction and remaining
 * distance come from the sum.
 * Writes into and returns `out`.
 */
export function addToBoost(
  dirX: number,
  dirY: number,
  remainingU: number,
  pushDirX: number,
  pushDirY: number,
  pushU: number,
  out: BoostVector = { dirX: 1, dirY: 0, remainingU: 0 },
): BoostVector {
  const bx = dirX * remainingU + pushDirX * pushU;
  const by = dirY * remainingU + pushDirY * pushU;
  const len = Math.sqrt(bx * bx + by * by);
  if (len < 1e-9) {
    out.dirX = dirX;
    out.dirY = dirY;
    out.remainingU = 0;
  } else {
    out.dirX = bx / len;
    out.dirY = by / len;
    out.remainingU = len;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// §1 World
// ---------------------------------------------------------------------------------------------

/** Half the map side: coordinates run from −half to +half (mechanics §1.1). */
export function worldHalfSize(size: number = SIM_DEFAULTS.world.size): number {
  return size / 2;
}

/** `(size / 14142.136)²`: how area-scaled counts grow with the map (mechanics §1.1). */
export function areaScale(size: number = SIM_DEFAULTS.world.size): number {
  const k = size / REFERENCE_WORLD_SIZE;
  return k * k;
}

/** A default-map count scaled to a map of side `size`, rounded (mechanics §1.1). */
export function scaleCountByArea(count: number, size: number = SIM_DEFAULTS.world.size): number {
  return Math.round(count * areaScale(size));
}

/**
 * How far a player cell's centre must stay inside each wall (mechanics §1.2): `r × (1 − overhang)`,
 * so with the default overhang 0.5 the centre stays r/2 inside and half the cell can hang over.
 */
export function playerBorderInset(
  r: number,
  overhang: number = SIM_DEFAULTS.world.borderOverhang,
): number {
  return r * (1 - overhang);
}

/**
 * Clamps one coordinate so it stays `inset` inside the walls at ±`half` (mechanics §1.2). Pellets,
 * ejected mass, viruses and spawners use `inset = r`; player cells use `playerBorderInset(r)`.
 */
export function clampToWorld(v: number, inset: number, half: number): number {
  const lo = -half + inset;
  const hi = half - inset;
  return lo > hi ? 0 : clamp(v, lo, hi);
}

/** Clamps a target coordinate to the map grown by `margin` on each side (mechanics §4.1). */
export function clampTarget(
  v: number,
  half: number = worldHalfSize(),
  margin: number = SIM_DEFAULTS.input.targetMargin,
): number {
  return clamp(v, -half - margin, half + margin);
}

// ---------------------------------------------------------------------------------------------
// §3 Mass and radius
// ---------------------------------------------------------------------------------------------

/** `r = 10·√m` (mechanics §3). */
export function radiusOf(mass: number): number {
  return RADIUS_PER_SQRT_MASS * Math.sqrt(mass);
}

/** `m = r² / 100` (mechanics §3). */
export function massOf(radius: number): number {
  return (radius * radius) / (RADIUS_PER_SQRT_MASS * RADIUS_PER_SQRT_MASS);
}

/** Radius after a cell of radius `R` absorbs one of radius `r`: `√(R² + r²)` (mechanics §3). */
export function combinedRadius(R: number, r: number): number {
  return Math.sqrt(R * R + r * r);
}

/** Mass labels and the score show `floor(m)` (mechanics §3, §13.1). */
export function displayMass(mass: number): number {
  return Math.floor(mass);
}

/** Score = `floor(Σm)` of a player's cells (mechanics §13.1). */
export function scoreOf(masses: Iterable<number>): number {
  let sum = 0;
  for (const m of masses) sum += m;
  return Math.floor(sum);
}

/** Largest pellet radius, used to widen static-grid queries (architecture §5). */
export const FOOD_MAX_RADIUS = radiusOf(SIM_DEFAULTS.food.maxMass);

// ---------------------------------------------------------------------------------------------
// §4 Movement
// ---------------------------------------------------------------------------------------------

/**
 * Maximum steering speed `speed(r) = 1930 · r^(−0.439)` u/s (mechanics §4.2): 423.7 u/s at mass 10.
 * The cell reaches it once the target is at least `speed / steer.gain` away.
 */
export function speedOf(
  r: number,
  coef: number = SIM_DEFAULTS.speed.coef,
  exponent: number = SIM_DEFAULTS.speed.exponent,
): number {
  return coef * Math.pow(r, exponent);
}

/**
 * One tick's direct move: `min(speed·dt, distance)`, never overshooting. Free-roam spectator
 * cameras use it (mechanics §15); player cells steer with `steerVelocity` instead.
 */
export function moveStep(
  distanceU: number,
  speedUPerSec: number,
  dtSec: number = tickDt(),
): number {
  return Math.min(speedUPerSec * dtSec, distanceU);
}

/**
 * Per-tick steering velocity easing factor `β = 1 − exp(−dt/τ)` (mechanics §4.2): 0.4353 at the
 * defaults (dt 0.04 s, τ 0.07 s). `τ ≤ 0` means no easing (β = 1). Computed once per room, never
 * per cell.
 */
export function steerBeta(
  dtSec: number = tickDt(),
  tauSec: number = SIM_DEFAULTS.steer.velTauSec,
): number {
  return tauSec > 0 ? 1 - Math.exp(-dtSec / tauSec) : 1;
}

/**
 * One tick's steering velocity update (mechanics §4.2), in place on `sv` (u/s), which it returns.
 * `(dx, dy)` is target minus centre and `d` its length. The desired velocity points at the target
 * with speed `min(vmax, gain·d)` (the proportional zone; 0 at d = 0), and the velocity closes the
 * fraction `beta` of the gap to it: `sv += (vdes − sv)·β`.
 */
export function steerVelocity(
  dx: number,
  dy: number,
  d: number,
  vmax: number,
  gain: number,
  beta: number,
  sv: Vec2,
): Vec2 {
  let vdx = 0;
  let vdy = 0;
  if (d > 0) {
    const k = Math.min(vmax, gain * d) / d;
    vdx = dx * k;
    vdy = dy * k;
  }
  sv.x += (vdx - sv.x) * beta;
  sv.y += (vdy - sv.y) * beta;
  return sv;
}

/**
 * Launch direction toward a target (mechanics §7, §9): the unit vector `(dx, dy)` (target minus
 * centre). If the target is within 1 u, the last movement direction `(lastX, lastY)` is used, or
 * +x if the cell never moved. Writes into and returns `out`.
 */
export function aimDirection(
  dx: number,
  dy: number,
  lastX: number,
  lastY: number,
  out: Vec2 = { x: 1, y: 0 },
): Vec2 {
  const d = Math.sqrt(dx * dx + dy * dy);
  if (d > 1) {
    out.x = dx / d;
    out.y = dy / d;
    return out;
  }
  const l = Math.sqrt(lastX * lastX + lastY * lastY);
  if (l > 1e-9) {
    out.x = lastX / l;
    out.y = lastY / l;
  } else {
    out.x = 1;
    out.y = 0;
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// §6 Eating
// ---------------------------------------------------------------------------------------------

/** Size rule: `r_eater > ratio · r_target` (mechanics §6.1; strict, so equal sizes never eat). */
export function canEatBySize(
  rEater: number,
  rTarget: number,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
): boolean {
  return rEater > ratio * rTarget;
}

/** Overlap rule: `distance < R_eater − r_target / 3` (mechanics §6.2; also used for merging). */
export function isDeepEnough(
  distanceU: number,
  rEater: number,
  rTarget: number,
  overlapDivisor: number = SIM_DEFAULTS.eat.overlapDivisor,
): boolean {
  return distanceU < rEater - rTarget / overlapDivisor;
}

/**
 * The full eat test for a sized target (player cell, ejected mass, virus, spawner): size rule and
 * overlap rule (mechanics §6.1–6.2). Pellets need only `isDeepEnough`.
 */
export function canEat(
  rEater: number,
  rTarget: number,
  distanceU: number,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
  overlapDivisor: number = SIM_DEFAULTS.eat.overlapDivisor,
): boolean {
  return rEater > ratio * rTarget && distanceU < rEater - rTarget / overlapDivisor;
}

/**
 * The eater mass that must be *exceeded* to eat `targetMass`: `targetMass × ratio²`
 * (mechanics §6.1: > 19.04 for ejected mass, > 132.25 for a virus, > 293.6 for a spawner).
 */
export function minMassToEat(
  targetMass: number,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
): number {
  return targetMass * ratio * ratio;
}

/** The target mass that must be *undercut* to be eaten by `eaterMass`: `eaterMass / ratio²`. */
export function maxEdibleMass(
  eaterMass: number,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
): number {
  return eaterMass / (ratio * ratio);
}

export interface AntiFeederParams {
  readonly enabled: boolean;
  readonly eaterMinMass: number;
  readonly preyMaxMass: number;
}

/**
 * Anti-feeder rule (mechanics §6.5): when enabled, a cell of ≥ 625 mass gains nothing from eating
 * another player's cell of ≤ 17 mass. Never applies to own-cell merges (the caller's job).
 */
export function antiFeederBlocksGain(
  eaterMass: number,
  preyMass: number,
  p: AntiFeederParams = SIM_DEFAULTS.antiFeeder,
): boolean {
  return p.enabled && eaterMass >= p.eaterMinMass && preyMass <= p.preyMaxMass;
}

// ---------------------------------------------------------------------------------------------
// §7 Splitting
// ---------------------------------------------------------------------------------------------

/** A cell may split when `m ≥ 36` (mechanics §7). */
export function canSplit(mass: number, minMass: number = SIM_DEFAULTS.split.minMass): boolean {
  return mass >= minMass;
}

/** The radius of each half after a split: `r / √2`. */
export function splitHalfRadius(r: number): number {
  return r * SQRT1_2;
}

// ---------------------------------------------------------------------------------------------
// §8 Merging and own-cell collision
// ---------------------------------------------------------------------------------------------

/**
 * `mergeDelay(r) = max(30, floor(0.2 · r))` seconds (mechanics §8.1, Appendix C:
 * m 500 → 44 s, 1,000 → 63 s, 22,500 → 300 s). `multiplier` is for variants (Rush: × 0.5).
 */
export function mergeDelay(
  r: number,
  baseSec: number = SIM_DEFAULTS.merge.baseSec,
  perRadiusSec: number = SIM_DEFAULTS.merge.perRadiusSec,
  multiplier = 1,
): number {
  return Math.max(baseSec, Math.floor(perRadiusSec * r)) * multiplier;
}

/** A cell can merge once `mergeClock ≥ mergeDelay(current r)` (mechanics §8.1). */
export function canMerge(
  mergeClockSec: number,
  r: number,
  baseSec: number = SIM_DEFAULTS.merge.baseSec,
  perRadiusSec: number = SIM_DEFAULTS.merge.perRadiusSec,
  multiplier = 1,
): boolean {
  return mergeClockSec >= mergeDelay(r, baseSec, perRadiusSec, multiplier);
}

/** Rigid-push overlap `o = (R + r) − d`; positive when the circles overlap (mechanics §8.2). */
export function pushOverlap(distanceU: number, rA: number, rB: number): number {
  return rA + rB - distanceU;
}

/**
 * The share of the overlap a cell moves in a rigid push: `other's mass / combined mass`
 * (mechanics §8.2). The light cell moves a lot, the heavy one barely moves; the two shares sum to 1.
 * Ejected pellets push each other with equal masses (share 0.5, mechanics §9.4).
 */
export function pushShare(selfMass: number, otherMass: number): number {
  const total = selfMass + otherMass;
  return total > 0 ? otherMass / total : 0.5;
}

/** Below this centre distance two cells count as concentric and push along +x (mechanics §8.2). */
export const SAME_CENTRE_EPSILON_U = 0.001;

// ---------------------------------------------------------------------------------------------
// §9 Ejecting mass
// ---------------------------------------------------------------------------------------------

/** A cell may eject when `m ≥ 35` (mechanics §9). */
export function canEject(mass: number, minMass: number = SIM_DEFAULTS.eject.minMass): boolean {
  return mass >= minMass;
}

/** A uniform aim jitter in `[−jitter, +jitter)` radians (mechanics §9: ±0.3 rad). */
export function ejectJitter(rng: Rng, jitterRad: number = SIM_DEFAULTS.eject.jitterRad): number {
  return rng.range(-jitterRad, jitterRad);
}

/** Radius of an ejected pellet (37.95 at the default 14.4 mass). */
export const EJECTED_RADIUS = radiusOf(SIM_DEFAULTS.eject.pelletMass);

// ---------------------------------------------------------------------------------------------
// §10 Viruses
// ---------------------------------------------------------------------------------------------

/**
 * A virus's feed count worked out from its radius (protocol §5.3):
 * `round((r²/100 − virusMass) / ejectPelletMass)`, never negative.
 */
export function virusFeedsFromRadius(
  r: number,
  virusMass: number = SIM_DEFAULTS.virus.mass,
  ejectPelletMass: number = SIM_DEFAULTS.eject.pelletMass,
): number {
  return Math.max(0, Math.round((massOf(r) - virusMass) / ejectPelletMass));
}

/** "Nearly full" look: at least `feedsToShoot − 2` feeds (architecture §9.3: 5 of 7). */
export function isVirusNearlyFull(
  feeds: number,
  feedsToShoot: number = SIM_DEFAULTS.virus.feedsToShoot,
): boolean {
  return feeds >= feedsToShoot - 2;
}

// --- §10.4 Pop distribution ---

/**
 * Pieces a pop may produce, counting the parent: `n = maxCells − cellCount + 1`
 * (mechanics §10.4; 15 cells → 2, 16 cells → 1 = no pop).
 */
export function popAllowedPieces(
  cellCount: number,
  maxCells: number = SIM_DEFAULTS.cells.max,
): number {
  return maxCells - cellCount + 1;
}

/**
 * The number of pieces `k` a pop of mass `M` with `n` allowed pieces actually makes
 * (mechanics §10.4): halve `k` while `M / (k − 1) < P`. 1 means no pop.
 */
export function popPieceCount(
  mass: number,
  allowed: number,
  minPiece: number = SIM_DEFAULTS.pop.minPiece,
): number {
  let k = Math.floor(allowed);
  while (k > 1 && mass / (k - 1) < minPiece) k = Math.floor(k / 2);
  return k <= 1 ? 1 : k;
}

/**
 * The ball.io pop distribution (mechanics §10.4). Returns the piece masses; they sum to `mass`.
 * `pieces[0]` stays the parent cell (keeps its position, id and colour). A result of length 1
 * (`[mass]`) means no pop.
 *
 * Reference results: (232, 16) → 8 × 29; (300, 16) → 16 × 18.75; (581, 16) → 290.5 + 15 × 19.37;
 * (10,100, 4) → 5050, 2525, 1262.5, 1262.5; (500, 2) → 250, 250.
 */
export function popPieces(
  mass: number,
  allowed: number,
  minPiece: number = SIM_DEFAULTS.pop.minPiece,
): number[] {
  const k = popPieceCount(mass, allowed, minPiece);
  if (k <= 1) return [mass];
  const pieces: number[] = [];
  let cur = mass;
  let rest = k;
  while (rest > 2) {
    const half = cur / 2;
    if (half <= minPiece || half >= cur - minPiece * (rest - 1)) break;
    pieces.push(half);
    cur -= half;
    rest -= 1;
  }
  const each = cur / rest;
  for (let i = 0; i < rest; i++) pieces.push(each);
  return pieces;
}

/**
 * Launch angle of new pop piece `i` (0-based among the `k − 1` new pieces, i.e. `pieces[i + 1]`):
 * `θ_i = θ0 + 2π·i/(k − 1)` (mechanics §10.4).
 */
export function popPieceAngle(i: number, pieceCount: number, theta0: number): number {
  return theta0 + (TAU * i) / (pieceCount - 1);
}

// ---------------------------------------------------------------------------------------------
// §5 Food
// ---------------------------------------------------------------------------------------------

/** Food target for a map of side `size` (mechanics §5: 4000 on the default map, scales with area). */
export function foodTarget(
  target: number = SIM_DEFAULTS.food.target,
  size: number = SIM_DEFAULTS.world.size,
): number {
  return scaleCountByArea(target, size);
}

/** A pellet's mass after one growth step (+1 every 60 s, capped at 5; mechanics §5). */
export function grownFoodMass(mass: number, maxMass: number = SIM_DEFAULTS.food.maxMass): number {
  return Math.min(maxMass, mass + 1);
}

// ---------------------------------------------------------------------------------------------
// §11 Decay
// ---------------------------------------------------------------------------------------------

/**
 * The decay factor over `dt` seconds: `(1 − rate)^(dt × mult)` (mechanics §11; 0.99991992 per
 * 40 ms tick). `mult` is the per-player decay multiplier (anti-team × bot soft cap): it scales the
 * rate in continuous time, so ×4 decays as if 4 normal ticks had passed.
 */
export function decayFactor(
  dtSec: number = tickDt(),
  ratePerSec: number = SIM_DEFAULTS.decay.ratePerSec,
  mult = 1,
): number {
  return Math.pow(1 - ratePerSec, dtSec * mult);
}

/** `m ← max(minMass, m × factor)`; no decay at or below the floor (mechanics §11). */
export function decayMass(
  mass: number,
  dtSec: number = tickDt(),
  ratePerSec: number = SIM_DEFAULTS.decay.ratePerSec,
  minMass: number = SIM_DEFAULTS.decay.minMass,
  mult = 1,
): number {
  if (mass <= minMass) return mass;
  return Math.max(minMass, mass * decayFactor(dtSec, ratePerSec, mult));
}

/** Decay half-life in seconds: `ln 2 / −ln(1 − rate)` (346.2 s at 0.2 %/s). */
export function decayHalfLifeSec(ratePerSec: number = SIM_DEFAULTS.decay.ratePerSec): number {
  return Math.LN2 / -Math.log(1 - ratePerSec);
}

/** Bot soft cap (mechanics §16.6): ×4 decay above 6000 total mass. */
export function botSoftCapMult(
  totalMass: number,
  softCap: number = BOT_DEFAULTS.massSoftCap,
  mult: number = BOT_DEFAULTS.softCapDecayMult,
): number {
  return totalMass > softCap ? mult : 1;
}

/**
 * Anti-team multiplier (mechanics §18): with `n` viruses eaten in the window,
 * `1 + max(0, n − (threshold − 1))`, capped at `maxMult` (3 viruses → ×2, 4 → ×3).
 */
export function antiTeamMult(
  virusesInWindow: number,
  threshold: number = SIM_DEFAULTS.antiTeam.threshold,
  maxMult: number = SIM_DEFAULTS.antiTeam.maxMult,
): number {
  return Math.min(maxMult, 1 + Math.max(0, virusesInWindow - (threshold - 1)));
}

// ---------------------------------------------------------------------------------------------
// §12 Spawning
// ---------------------------------------------------------------------------------------------

/** A spawn point is rejected within `r_new + r_other + margin` of another object (mechanics §12.1). */
export function spawnClearance(
  rNew: number,
  rOther: number,
  margin: number = SIM_DEFAULTS.spawn.safeMargin,
): number {
  return rNew + rOther + margin;
}

/** True if a spawn at centre distance `distanceU` from another object is safe (mechanics §12.1). */
export function isSpawnSafe(
  distanceU: number,
  rNew: number,
  rOther: number,
  margin: number = SIM_DEFAULTS.spawn.safeMargin,
): boolean {
  return distanceU >= rNew + rOther + margin;
}

/**
 * The clearance margin a spawn of radius `rNew` keeps from an object of radius `rOther`
 * (mechanics §12.1): `threatMargin` from a player cell big enough to eat the newcomer
 * (`canEatBySize`), so it cannot split-kill or catch it at once; `safeMargin` from anything else.
 */
export function spawnMargin(
  rNew: number,
  rOther: number,
  isPlayerCell: boolean,
  safeMargin: number = SIM_DEFAULTS.spawn.safeMargin,
  threatMargin: number = SIM_DEFAULTS.spawn.threatMargin,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
): number {
  return isPlayerCell && canEatBySize(rOther, rNew, ratio)
    ? Math.max(safeMargin, threatMargin)
    : safeMargin;
}

/** Mass of a player spawned from an ejected pellet: `max(startMass, pelletMass)` (mechanics §12.2). */
export function ejectedSpawnMass(
  pelletMass: number,
  startMass: number = SIM_DEFAULTS.mass.start,
): number {
  return Math.max(startMass, pelletMass);
}

// ---------------------------------------------------------------------------------------------
// §13 Statistics
// ---------------------------------------------------------------------------------------------

/** Seconds added to "time on the board" per leaderboard update in the top 10 (mechanics §13.2). */
export function boardTimePerUpdateSec(
  everyTicks: number = SIM_DEFAULTS.leaderboard.everyTicks,
  tickRate: number = SIM_DEFAULTS.tickRate,
): number {
  return everyTicks / tickRate;
}

/**
 * Appends a mass-history sample (mechanics §13.2): when the list is full, it keeps every second
 * sample (indices 0, 2, 4, …) and the interval doubles. Mutates `samples`; returns the new interval.
 */
export function pushMassSample(
  samples: number[],
  intervalSec: number,
  value: number,
  maxSamples: number = MASS_HISTORY.maxSamples,
): number {
  let interval = intervalSec;
  if (samples.length >= maxSamples) {
    let w = 0;
    for (let i = 0; i < samples.length; i += 2) samples[w++] = samples[i] as number;
    samples.length = w;
    interval *= 2;
  }
  samples.push(value);
  return interval;
}

// ---------------------------------------------------------------------------------------------
// §14 View and camera
// ---------------------------------------------------------------------------------------------

export interface ViewParams {
  readonly baseW: number;
  readonly baseH: number;
  readonly refRadius: number;
  readonly exponent: number;
  readonly minScale: number;
  readonly margin: number;
  readonly minWheelZoom: number;
}

/**
 * View scale `s = clamp(min(1, 64 / Σr)^0.4, 0.15, 1)` (mechanics §14.1). `Σr` is the sum of the
 * player's cell radii; with no cells (Σr ≤ 0) the scale is 1.
 */
export function viewScale(sumRadius: number, p: ViewParams = SIM_DEFAULTS.view): number {
  if (!(sumRadius > 0)) return 1;
  const s = Math.pow(Math.min(1, p.refRadius / sumRadius), p.exponent);
  return clamp(s, p.minScale, 1);
}

/** The wheel zoom the server honours: clamped to [minWheelZoom, max] (mechanics §14.4). */
export function clampWheelZoom(
  wheelZoom: number,
  min: number = SIM_DEFAULTS.view.minWheelZoom,
  max: number = WHEEL_ZOOM.max,
): number {
  return Number.isFinite(wheelZoom) ? clamp(wheelZoom, min, max) : 1;
}

export interface HalfExtents {
  halfW: number;
  halfH: number;
}

/**
 * Half-size of the server view box (mechanics §14.2, protocol §6.1):
 * `(baseW / (s·zMin)) × (baseH / (s·zMin))` plus `margin` on every side, `zMin = min(1, wheelZoom)`.
 * Pass `extraMargin` (e.g. the 300 u keep margin) to get the keep rectangle.
 */
export function viewHalfExtents(
  scale: number,
  wheelZoom = 1,
  p: ViewParams = SIM_DEFAULTS.view,
  extraMargin = 0,
  out: HalfExtents = { halfW: 0, halfH: 0 },
): HalfExtents {
  const zMin = Math.min(1, clampWheelZoom(wheelZoom, p.minWheelZoom));
  const k = scale * zMin;
  out.halfW = p.baseW / k / 2 + p.margin + extraMargin;
  out.halfH = p.baseH / k / 2 + p.margin + extraMargin;
  return out;
}

/**
 * Client zoom target: `s × max(W/1920, H/1080) × wheelZoom` (mechanics §14.3), W and H in CSS px.
 */
export function cameraZoom(
  scale: number,
  canvasW: number,
  canvasH: number,
  wheelZoom = 1,
  baseW: number = SIM_DEFAULTS.view.baseW,
  baseH: number = SIM_DEFAULTS.view.baseH,
): number {
  return scale * Math.max(canvasW / baseW, canvasH / baseH) * wheelZoom;
}

/**
 * Frame-rate independent easing: the fraction of the remaining distance to cover in `dtMs` for a
 * given half-life, `1 − 0.5^(dt / halfLife)` (mechanics §14.3: 17 ms camera, 110 ms zoom).
 */
export function easeFactor(
  dtMs: number,
  halfLifeMs: number = CAMERA_EASE.positionHalfLifeMs,
): number {
  if (!(halfLifeMs > 0)) return 1;
  return 1 - Math.pow(0.5, dtMs / halfLifeMs);
}

/** Applies wheel notches: ×1.1 per notch up (positive), ÷1.1 per notch down, clamped to [0.8, 4]. */
export function applyWheelNotches(wheelZoom: number, notches: number): number {
  return clamp(wheelZoom * Math.pow(WHEEL_ZOOM.step, notches), WHEEL_ZOOM.min, WHEEL_ZOOM.max);
}

/** Wheel zoom as reported in OPTIONS: rounded to 0.01 (mechanics §14.4). */
export function roundWheelZoom(wheelZoom: number): number {
  return Math.round(wheelZoom * 100) / 100;
}

// ---------------------------------------------------------------------------------------------
// §16 Bots (formulas only; the brain lives in the server)
// ---------------------------------------------------------------------------------------------

/** Edge distance used by the influence sum: `max(1, d − R_C − r_o)` (mechanics §16.4). */
export function botEdgeDistance(distanceU: number, rSelf: number, rOther: number): number {
  return Math.max(BOT_TUNING.minEdgeDistanceU, distanceU - rSelf - rOther);
}

/** Weight of an enemy cell the bot can eat: `+4 · log10(1 + m)` (mechanics §16.4). */
export function botPreyWeight(preyMass: number): number {
  return BOT_TUNING.weights.preyPerLogMass * Math.log10(1 + preyMass);
}

/**
 * Weight of a threat: `−6 · log10(1 + m) × threatMult`, doubled if it could also split-kill
 * (mechanics §16.4).
 */
export function botThreatWeight(
  threatMass: number,
  threatMult: number,
  canSplitKillMe: boolean,
): number {
  const w = BOT_TUNING.weights.threatPerLogMass * Math.log10(1 + threatMass) * threatMult;
  return canSplitKillMe ? w * BOT_TUNING.weights.threatSplitKillFactor : w;
}

/**
 * Wall push weight for a rim `distToWallU` from a wall (mechanics §16.4 step 3). With a threat
 * near (`threatNear`, the default): `2 × (1 − dist/300)` within 300 u. Without one: the short calm
 * push `0.1 × (1 − dist/60)` within 60 u, weaker than a nearby pellet. 0 beyond the range.
 */
export function botWallWeight(distToWallU: number, threatNear = true): number {
  const range = threatNear ? BOT_TUNING.wallRangeU : BOT_TUNING.calmWallRangeU;
  const weight = threatNear ? BOT_TUNING.wallWeight : BOT_TUNING.calmWallWeight;
  return distToWallU < range ? weight * (1 - Math.max(0, distToWallU) / range) : 0;
}

/**
 * Could a cell of radius `rAttacker` split-kill one of radius `rTarget` at distance `d`?
 * Its half radius must still pass the size rule and `d < splitBoost + rAttacker`
 * (mechanics §16.4 threat doubling).
 */
export function couldSplitKill(
  rAttacker: number,
  rTarget: number,
  distanceU: number,
  ratio: number = SIM_DEFAULTS.eat.radiusRatio,
  splitBoost: number = SIM_DEFAULTS.split.boost,
): boolean {
  return rAttacker * SQRT1_2 > ratio * rTarget && distanceU < splitBoost + rAttacker;
}

/** Split-kill reach: `0.85 × 780 + C.r/√2` (mechanics §16.5). */
export function botSplitReach(r: number, splitBoost: number = SIM_DEFAULTS.split.boost): number {
  return BOT_TUNING.splitReachFactor * splitBoost + r * SQRT1_2;
}

// ---------------------------------------------------------------------------------------------
// §17 Modes
// ---------------------------------------------------------------------------------------------

/** Per-tick probability of a Poisson event: `p = 1 − e^(−rate·dt)` (mechanics §17.4). */
export function poissonTickChance(ratePerSec: number, dtSec: number = tickDt()): number {
  return 1 - Math.exp(-ratePerSec * dtSec);
}

/**
 * Battle Royale out-of-zone loss over `dt` (mechanics §17.5): `(10 + 1 % × m) × dt` mass.
 */
export function outOfZoneMassLoss(
  mass: number,
  dtSec: number = tickDt(),
  damagePerSec: number = MODE_DEFAULTS.battleroyale.outside.damagePerSec,
  damageFractionPerSec: number = MODE_DEFAULTS.battleroyale.outside.damageFractionPerSec,
): number {
  return (damagePerSec + damageFractionPerSec * mass) * dtSec;
}

/** Linear zone shrink: the radius `t ∈ [0, 1]` of the way from `fromR` to `toR` (mechanics §17.5). */
export function zoneRadiusAt(fromR: number, toR: number, t: number): number {
  return fromR + (toR - fromR) * clamp(t, 0, 1);
}
